/**
 * PageToolbar Component
 *
 * Standardized toolbar for list pages with consistent spacing and layout.
 * Provides left and right sections with proper gap spacing and alignment.
 *
 * Usage:
 * ```tsx
 * <PageToolbar
 *   left={<FilterControls />}
 *   right={<Button>Add Item</Button>}
 * />
 * ```
 */

import { TopToolbar } from 'react-admin';
import { Box } from '@mui/material';
import { ReactNode } from 'react';

interface PageToolbarProps {
  /** Content for left section (filters, info, etc.) */
  left?: ReactNode;
  /** Content for right section (actions, buttons) */
  right?: ReactNode;
}

export const PageToolbar = ({ left, right }: PageToolbarProps) => {
  return (
    <TopToolbar
      sx={{
        width: '100%',
        justifyContent: 'space-between',
        paddingTop: '4px',
        paddingBottom: '4px',
      }}
    >
      {/* Left slot: sizes to content, pushed to left edge */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          marginRight: 'auto',
        }}
      >
        {left}
      </Box>

      {/* Right slot: sizes to content, pushed to right edge */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          marginLeft: 'auto',
        }}
      >
        {right}
      </Box>
    </TopToolbar>
  );
};
